/**
 * Settings Local Profile - Profile Editing
 *
 * Allows editing display name, avatar, and viewing profile info.
 * Electron-only component.
 *
 * @module @humanizer/studio/components/settings/SettingsLocalProfile
 */

import { useState, useEffect, useCallback } from 'react';
import type { SanitizedProfile, OsUserInfo } from '../../types/electron';
import { LocalAdminOverview } from './LocalAdminOverview';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SettingsLocalProfile() {
  const [profile, setProfile] = useState<SanitizedProfile | null>(null);
  const [osInfo, setOsInfo] = useState<OsUserInfo | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────────────────────────────────

  const loadProfile = useCallback(async () => {
    if (!window.electronLocalAuth) {
      setLoading(false);
      return;
    }

    try {
      const [activeProfile, osUserInfo] = await Promise.all([
        window.electronLocalAuth.getActiveProfile(),
        window.electronLocalAuth.resolveOsUser(),
      ]);

      if (activeProfile) {
        setProfile(activeProfile);
        setDisplayName(activeProfile.displayName);
      }
      setOsInfo(osUserInfo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // ─────────────────────────────────────────────────────────────────────────
  // SAVE HANDLER
  // ─────────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!profile || !window.electronLocalAuth) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await window.electronLocalAuth.updateProfile(profile.id, {
        displayName: displayName.trim(),
      });

      if (result.success && result.profile) {
        setProfile(result.profile);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // AVATAR SELECTION
  // ─────────────────────────────────────────────────────────────────────────

  const handleSelectAvatar = async () => {
    if (!profile || !window.electronAPI) return;

    const filePath = await window.electronAPI.dialog.selectFile({
      filters: [{ name: 'Images', extensions: ['png', 'jpg', 'jpeg', 'webp'] }],
    });

    if (filePath && window.electronLocalAuth) {
      const result = await window.electronLocalAuth.updateProfile(profile.id, {
        avatarPath: filePath,
      });
      if (result.success && result.profile) {
        setProfile(result.profile);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="settings-section">
        <div className="settings-loading">
          <span className="settings-loading__spinner" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="settings-section">
        <div className="settings-alert settings-alert--error">
          No active profile found
        </div>
      </div>
    );
  }

  const hasChanges = displayName.trim() !== profile.displayName;

  return (
    <div className="settings-section">
      <div className="settings-section__header">
        <h2 className="settings-section__title">Local Profile</h2>
        <p className="settings-section__description">
          Manage your local identity on this device
        </p>
      </div>

      <div className="settings-section__content">
        {error && (
          <div className="settings-alert settings-alert--error">{error}</div>
        )}
        {success && (
          <div className="settings-alert settings-alert--success">
            Profile updated successfully
          </div>
        )}

        {/* Profile Card */}
        <div className="settings-card">
          <h3 className="settings-card__title">Identity</h3>
          <div className="settings-card__content">
            {/* Avatar */}
            <div className="settings-form__field">
              <label>Avatar</label>
              <div className="settings-profile__avatar-row">
                <div className="settings-profile__avatar">
                  {profile.avatarPath ? (
                    <img
                      src={profile.avatarPath}
                      alt={profile.displayName}
                      className="settings-profile__avatar-img"
                    />
                  ) : (
                    <span className="settings-profile__avatar-initials">
                      {profile.displayName.substring(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={handleSelectAvatar}
                >
                  Change Avatar
                </button>
              </div>
            </div>

            {/* Display Name */}
            <div className="settings-form__field">
              <label htmlFor="displayName">Display Name</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
              />
              <p className="settings-form__hint">
                Shown in the profile picker and top bar
              </p>
            </div>

            {/* OS Username (read-only) */}
            <div className="settings-form__field">
              <label>OS Username</label>
              <input
                type="text"
                value={osInfo?.username || profile.osUsername}
                disabled
                readOnly
              />
              <p className="settings-form__hint">
                Detected from your operating system (read-only)
              </p>
            </div>

            {/* Role (read-only) */}
            <div className="settings-form__field">
              <label>Role</label>
              <input
                type="text"
                value={profile.localRole.charAt(0).toUpperCase() + profile.localRole.slice(1)}
                disabled
                readOnly
              />
              <p className="settings-form__hint">
                {profile.localRole === 'owner'
                  ? 'Full access to all settings and profile management'
                  : profile.localRole === 'restricted'
                    ? 'Limited access controlled by the profile owner'
                    : 'Standard access to most features'}
              </p>
            </div>

            {/* Cloud Link Status */}
            {profile.cloudLink && (
              <div className="settings-form__field">
                <label>Cloud Account</label>
                <input
                  type="text"
                  value={`${profile.cloudLink.email} (${profile.cloudLink.tier})`}
                  disabled
                  readOnly
                />
                <p className="settings-form__hint">
                  Linked to humanizer.com — manage in Cloud Account settings
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="settings-actions">
          <button
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Setup Wizard */}
        {window.isElectron && window.electronApp && (
          <div className="settings-card mt-lg">
            <h3 className="settings-card__title">Setup Wizard</h3>
            <div className="settings-card__content">
              <p className="settings-form__hint mb-md">
                Re-run the initial setup wizard to change your database connection,
                media directory, backup directory, or archive path. Re-running will
                generate a new database password and update your system keychain.
                Changing file locations does <strong>not</strong> move existing
                data — you would need to move files manually and re-import.
              </p>
              <button
                className="btn btn--secondary"
                onClick={() => window.dispatchEvent(new CustomEvent('show-onboarding'))}
              >
                Open Setup Wizard
              </button>
            </div>
          </div>
        )}

        {/* Usage Overview */}
        <LocalAdminOverview />
      </div>
    </div>
  );
}
